import * as _ from "lodash";
import {
    AttributeFilterType,
    DataLocation,
    IdsAttribute,
    IdsFilterSpec,
    IdsMetric,
    IdsNode,
    IdsRow,
    IMetricDataSet,
    IMetricDataSetProvider
} from "./PA.MetricDataSet.IF";
import { IProgressProvider } from "../PA.Core/PA.Core.IF.Basic";
import { BehaviorSubject } from "rxjs";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { ExecutionSerializer } from "../PA.Core/Basic/ExecutionSerializer";
import { sha1 } from "object-hash";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { FilterUtils } from "../PA.Core/Basic/Filter";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ErrorMessage_restate, ExceptionMessage_restate } from "../PA.Core/Basic/MessageUtils";
import { ExpressionUtils } from "../PA.Core/Basic/ExpressionUtils"

const bDebugSerializers = false;
const rowThresholdCountDefault = 5000;

export interface IMetricDataSetOptions {
    name?: string;
    attributes?: string[];
    metrics?: string[];
    rowThresholdCountDefault?: number;
    dataLoadingPaused?: boolean;
}

interface ILoadScope {
    attributes: string[];
    attributesDataProvider: string[];
    attributesLocal: string[];
    metrics: string[];
    metricsDataProvider: string[];
    metricsLocal: string[];
    filters: IdsFilterSpec[];
    rowThresholdCount: number;

    attributeHash: string;
    attributeDataProviderHash: string;
    attributeLocalHash: string;
    metricHash: string;
    metricsDataProviderHash: string;
    metricsLocalHash: string;
    filterHash: string;
}

export class MetricDataSet implements IMetricDataSet {
    private createOptions: IMetricDataSetOptions;
    private dataProvider: IMetricDataSetProvider;

    // Metadata from Data Provider
    private dpAttributes: { [attributeId: string]: IdsAttribute };
    private dpMetrics: { [metricId: string]: IdsMetric };
    private lclAttributes: { [attributeId: string]: IdsAttribute };
    private lclMetrics: { [metricId: string]: IdsMetric };
    private dctSimpleType: { [symbolId: string]: "number" | "string" | "date" };

    // For Initialization
    private name: string;
    private loadComplete: boolean;
    private onLoadComplete: Promise<void>;
    private initMessageProvider: MessageProvider;

    // For Data Loading
    private loadProgressProvider: IProgressProvider;
    private staticScope: ILoadScope;
    private queueScope: ILoadScope;
    private loadedScope: ILoadScope;
    private bForceReload: boolean;
    private bDataLoadingPaused: boolean;

    private filteredRows: IdsRow[];
    private onFilteredRowsChanged: BehaviorSubject<IMetricDataSet>;
    private allRows: IdsRow[];
    private onAllRowsChanged: BehaviorSubject<IMetricDataSet>;
    private dctUniqueLocalValues: { [attributeId: string]: any[] };
    private dctUniqueLocalNodes: { [attributeId: string]: IdsNode[] };
    private dctUniqueRemoteValues: { [attributeId: string]: any[] };

    private onFilterChanged: BehaviorSubject<IMetricDataSet>;

    private esUpdateData: ExecutionSerializer;

    constructor(dataProvider: IMetricDataSetProvider, options?: IMetricDataSetOptions) {
        this.dataProvider = dataProvider;
        this.createOptions = options || {};
        this.name = options?.name || dataProvider?.Name + " Data Set";
        // init
        this.loadComplete = false;
        this.onLoadComplete = undefined;
        this.initMessageProvider = new MessageProvider();

        // Data Loading
        this.loadProgressProvider = new ProgressProvider();
        this.filteredRows = [];
        this.onFilteredRowsChanged = new BehaviorSubject<IMetricDataSet>(undefined);
        this.allRows = [];
        this.onAllRowsChanged = new BehaviorSubject<IMetricDataSet>(undefined);
        this.onFilterChanged = new BehaviorSubject<IMetricDataSet>(undefined);
        this.dctUniqueLocalValues = {};
        this.dctUniqueRemoteValues = {};

        this.esUpdateData = new ExecutionSerializer(this, this.loadData.bind(this), [{}], 500, bDebugSerializers);
        this.bForceReload = false;
        this.bDataLoadingPaused = false;
        if (_.isBoolean(this.createOptions?.dataLoadingPaused))
            this.bDataLoadingPaused = this.createOptions?.dataLoadingPaused;

        this.queueScope = {
            attributes: [],
            attributesDataProvider: [],
            attributesLocal: [],
            metrics: [],
            metricsDataProvider: [],
            metricsLocal: [],
            filters: [],
            rowThresholdCount:  rowThresholdCountDefault,
            attributeHash: "",
            attributeDataProviderHash: "",
            attributeLocalHash: "",
            metricHash: "",
            metricsDataProviderHash: "",
            metricsLocalHash: "",
            filterHash: ""
        };
        this.loadedScope = {
            attributes: [],
            attributesDataProvider: [],
            attributesLocal: [],
            metrics: [],
            metricsDataProvider: [],
            metricsLocal: [],
            filters: [],
            rowThresholdCount: rowThresholdCountDefault,
            attributeHash: "",
            attributeDataProviderHash: "",
            attributeLocalHash: "",
            metricHash: "",
            metricsDataProviderHash: "",
            metricsLocalHash: "",
            filterHash: ""
        };
        this.staticScope = {
            attributes: [],
            attributesDataProvider: [],
            attributesLocal: [],
            metrics: [],
            metricsDataProvider: [],
            metricsLocal: [],
            filters: [],
            rowThresholdCount: 0,
            attributeHash: "",
            attributeDataProviderHash: "",
            attributeLocalHash: "",
            metricHash: "",
            metricsDataProviderHash: "",
            metricsLocalHash: "",
            filterHash: ""
        };

        if (_.isArray(this.createOptions?.attributes) && !_.isEmpty(this.createOptions?.attributes))
            this.queueScope.attributes = _.clone(this.createOptions.attributes);

        if (_.isArray(this.createOptions?.metrics) && !_.isEmpty(this.createOptions?.metrics))
            this.queueScope.metrics = _.clone(this.createOptions.metrics);

        if (_.isNumber(this.createOptions.rowThresholdCountDefault))
            this.queueScope.rowThresholdCount = this.createOptions.rowThresholdCountDefault;

        this.dpAttributes = {}
        this.lclAttributes = {}
        this.dpMetrics = {}
        this.lclMetrics = {}
        this.dctSimpleType = {}
        this.dctUniqueLocalNodes = {}
        this.dctUniqueLocalNodes = {}
        this.dctUniqueRemoteValues = {}
    }

    get Name(): string {
        return this.name;
    }

    get bLoaded(): boolean {
        return this.loadComplete;
    }

    OnLoadComplete(): Promise<void> {
        const self = this;

        if (this.onLoadComplete)
            return this.onLoadComplete;

        const rv = (this.onLoadComplete = (async () => {
            try {
                // Wait for the data provider to Load if it's not
                if (!self.dataProvider.bLoaded) await self.dataProvider.OnLoadComplete();

                if (!self.dataProvider.bLoaded) {
                    ErrorMessage_restate(self.initMessageProvider, true, "DPNotLoaded", "Data Provider wasn't loaded");
                    return;
                }

                const arrAttrs = this.dataProvider.getAttributes();
                this.dpAttributes = _.keyBy(arrAttrs, (a: IdsAttribute) => a.Id);
                const arrMtrcs = this.dataProvider.getMetrics();
                this.dpMetrics = _.keyBy(arrMtrcs, (a: IdsMetric) => a.Id);

                this.dctSimpleType = {};
                _.forEach(arrAttrs, (atr) => {
                    switch (atr.Type) {
                        case "string":
                        case "sapdate":
                            this.dctSimpleType[atr.Id] = "string";
                            return;

                        case "number":
                        case "sapdtts":
                        case "node":
                            this.dctSimpleType[atr.Id] = "number";
                            return;
                        case "date":
                            this.dctSimpleType[atr.Id] = "date";
                            return;
                    }

                });
                _.forEach(arrMtrcs, (mtrc) => {
                    this.dctSimpleType[mtrc.Id] = "number";
                });
                self.loadComplete = true;
            } catch (e) {
                ExceptionMessage_restate(self.initMessageProvider, e);
            }
        })());

        return rv;
    }

    // Parent Data Provider, use to access full set of available metadata.
    get DataProvider(): IMetricDataSetProvider {
        return this.dataProvider;
    }

    // Loading Status API & Events
    //-- changing server side filters, thresholds or structure (metrics, attributes) will trigger Loading
    get LoadProgressProvider(): IProgressProvider {
        return this.loadProgressProvider;
    }

    get RowCountThreshold(): number {
        return this.queueScope.rowThresholdCount;
    }

    setRowCountThreshold(threshold: number): void {
        this.queueScope.rowThresholdCount = threshold;
        this.QueueDataLoadUpdate();
    }

    forceReload(): void {
        this.bForceReload = true;
        this.QueueDataLoadUpdate();
    }

    // Metadata API
    getAttribute(attributeId: string): IdsAttribute {
        if (attributeId in this.dpAttributes) return this.dpAttributes[attributeId];
        if (attributeId in this.lclAttributes) return this.lclAttributes[attributeId];
    }

    getMetric(metricId: string): IdsMetric {
        if (metricId in this.dpMetrics) return this.dpMetrics[metricId];
        if (metricId in this.lclMetrics) return this.lclMetrics[metricId];
    }

    getAvailableAttributes(): IdsAttribute[] {
        return _.filter(_.union(_.values(this.dpAttributes), _.values(this.lclAttributes)), (atr) => !_.isEmpty(atr));
    }

    getAvailableMetrics(): IdsMetric[] {
        return _.filter(_.union(_.values(this.dpMetrics), _.values(this.lclMetrics)), (mtr) => !_.isEmpty(mtr));
    }

    getSymbolType(symbolId: string): "Metric" | "Attribute" | undefined {
        switch (true) {
            case (symbolId in this.dpMetrics):
            case (symbolId in this.lclMetrics):
                return "Metric";
            case (symbolId in this.dpAttributes):
            case (symbolId in this.lclAttributes):
                return "Attribute";
        }
    }


    addComputedAttributes(attribute: IdsAttribute | IdsAttribute[]): void {
        if (_.isEmpty(attribute)) return;

        if (!_.isArray(attribute)) attribute = [attribute];

        _.remove(attribute, (a: IdsAttribute) => _.isEmpty(a) || !a?.bAttributeComputed);

        // remove any attributes that conflict with the data provider's ids
        _.remove(attribute, (a: IdsAttribute) => a.Id in this.dpAttributes || a.Id in this.dpMetrics);

        // remove any attributes that conflict with the local computed metrics
        _.remove(attribute, (a: IdsAttribute) => a.Id in this.lclMetrics);

        // we could do further checking, but rather be resilient in loading code

        // replace any existing attributes
        _.forEach(attribute, (atr) => {
            this.lclAttributes[atr.Id] = atr;
            switch (atr.Type) {
                case "string":
                case "sapdate":
                    this.dctSimpleType[atr.Id] = "string";
                    return;

                case "number":
                case "sapdtts":
                case "node":
                    this.dctSimpleType[atr.Id] = "number";
                    return;
                case "date":
                    this.dctSimpleType[atr.Id] = "date";
                    return;
            }
        });
    }

    removeComputedAttributes(attribute: IdsAttribute | IdsAttribute[]): void {
        if (_.isEmpty(attribute)) return;

        if (!_.isArray(attribute)) attribute = [attribute];

        // Only remove if exists
        _.remove(attribute, (a: IdsAttribute) => !(a.Id in this.lclAttributes));
        // Don't remove if it's Loaded or Queued
        _.remove(attribute, (a: IdsAttribute) => {
            if (_.includes(this.loadedScope.attributes, a.Id)) return true;
            if (_.includes(this.queueScope.attributes, a.Id)) return true;
            return false;
        });
        _.forEach(attribute, (atr) => {
            delete this.lclAttributes[atr.Id];
            delete this.dctSimpleType[atr.Id];
        });
    }

    addComputedMetrics(metric: IdsMetric | IdsMetric[]): void {
        if (_.isEmpty(metric)) return;

        if (!_.isArray(metric)) metric = [metric];

        _.remove(metric, (a: IdsMetric) => _.isEmpty(a) || !a?.bMetricComputed);

        // remove any attributes that conflict with the data provider's ids
        _.remove(metric, (a: IdsMetric) => a.Id in this.dpAttributes || a.Id in this.dpMetrics);

        // remove any metrics that conflict with the local computed attributes
        _.remove(metric, (a: IdsMetric) => a.Id in this.lclAttributes);

        // we could do further checking, but rather be resilient in loading code

        // replace any existing attributes
        _.forEach(metric, (mtr) => {
            this.lclMetrics[mtr.Id] = mtr;
            this.dctSimpleType[mtr.Id] = "number";
        });

    }

    removeComputedMetrics(metric: IdsMetric | IdsMetric[]): void {
        if (_.isEmpty(metric)) return;

        if (!_.isArray(metric)) metric = [metric];

        // Only remove if exists
        _.remove(metric, (a: IdsMetric) => !(a.Id in this.lclMetrics));

        // Don't remove if it's Loaded or Queued
        _.remove(metric, (a: IdsMetric) => {
            if (_.includes(this.loadedScope.metrics, a.Id)) return true;
            if (_.includes(this.queueScope.metrics, a.Id)) return true;
            return false;
        });
        _.forEach(metric, (mtr) => {
            delete this.lclMetrics[mtr.Id];
            delete this.dctSimpleType[mtr.Id];
        });
    }

    // Structure API
    getAttributes(bLoaded?: boolean): IdsAttribute[] {
        const arrIds = bLoaded ? this.loadedScope.attributes : this.queueScope.attributes;
        const attrs = _.map(arrIds, (a) => this.getAttribute(a));
        return _.filter(attrs, (a) => !_.isEmpty(a));
    }

    getMetrics(bLoaded?: boolean): IdsMetric[] {
        const mtrIds = bLoaded ? this.loadedScope.metrics : this.queueScope.metrics;
        const mtrs = _.map(mtrIds, (a) => this.getMetric(a));
        return _.filter(mtrs, (a) => !_.isEmpty(a));
    }

    setRowStructure(arrAttributeIds: string[], arrMetricIds: string[]): void {
        this.queueScope.attributes = _.clone(arrAttributeIds);
        this.queueScope.metrics = _.clone(arrMetricIds);
        this.QueueDataLoadUpdate();
    }

    // Query Attribute Domain Values
    async getAttributeDistinctValues(
        attributeId: string,
        location: DataLocation = "LocalLoaded",
        valueFilterIDs?: string[],
    ): Promise<number[] | string[] | Date[]> {
        const attr = this.getAttribute(attributeId)
        if (_.isEmpty(attr)) return []

        switch (location) {
            case "LocalLoaded":
                if (attributeId in this.dctUniqueLocalValues) return _.clone(this.dctUniqueLocalValues[attributeId])

                this.updateLocalDistinctValues(attributeId, this.dctUniqueLocalValues, this.allRows)
                return _.clone(this.dctUniqueLocalValues[attributeId] || [])

            case "RemoteAvailable": {
                if (_.isEmpty(valueFilterIDs)) {
                    if (attributeId in this.dctUniqueRemoteValues)
                        return _.clone(this.dctUniqueRemoteValues[attributeId])

                    // Note: May not be atomic
                    await this.updateRemoteDistinctValues(attributeId);
                    return _.clone(this.dctUniqueRemoteValues[attributeId] || []);
                }
                // if there are value filters to apply, then we need to not cache them and retrieve them from the dataprovider each time
                // NOTE: This can be optimized with a separate cache in the future
                const attr = this.getAttribute(attributeId)
                if (_.isEmpty(attr)) return []
                let indexer = function (s: unknown): string {
                    return <string>s
                }
                if (attr.Type == "date")
                    indexer = function (s: any): string {
                        return <string>DateUtils.convertToSAPBW(s)
                    }

                const dct: { [id: string]: any } = {}
                const valueFilters = _.filter(
                    this.queueScope.filters,
                    (a) => a.type == "static" || valueFilterIDs.includes(a.id),
                )
                const uniqueValues = await this.dataProvider.getDistinctValues(attributeId, valueFilters)
                _.forEach(uniqueValues, (id) => {
                    const idx = indexer(id)
                    if (idx in dct) return
                    dct[idx] = id
                })

                return _.values(dct)
            }

            default:
        }

        return []
    }

    private updateLocalDistinctValues(
        attributeId: string,
        tgt: { [attributeId: string]: any[] },
        rows: IdsRow[]
    ): void {
        const attr = this.getAttribute(attributeId);
        if (_.isEmpty(attr)) return;

        const dct: { [id: string]: any } = {};
        let indexer = function(s: any): string {
            return <string>s;
        };
        if (attr.Type == "date")
            indexer = function(s: any): string {
                return <string>DateUtils.convertToSAPBW(s);
            };

        _.forEach(rows, (row) => {
            const atrValue = this.getRowAttribute(row, attributeId);
            const idx = indexer(atrValue);
            if (idx in dct) return;
            dct[idx] = atrValue;
        });
        tgt[attributeId] = _.values(dct);
    }

    private async updateRemoteDistinctValues(attributeId: string): Promise<void> {
        const attr = this.getAttribute(attributeId);
        if (_.isEmpty(attr)) return;

        const dct: { [id: string]: any } = {};
        let indexer = function(s: any): string {
            return <string>s;
        };
        if (attr.Type == "date")
            indexer = function(s: any): string {
                return <string>DateUtils.convertToSAPBW(s);
            };

        const staticFilters = _.filter(this.queueScope.filters, (a) => a.type == 'static')
        const uniqueValues = await this.dataProvider.getDistinctValues(attributeId, staticFilters);

        _.forEach(uniqueValues, (id) => {
            const idx = indexer(id);
            if (idx in dct) return;
            dct[idx] = id;
        });
        this.dctUniqueRemoteValues[attributeId] = _.values(dct);
    }

    async getAttributeNodes(
        attributeId: string,
        location: DataLocation = "LocalLoaded",
        valueFilterIDs?: string[]
    ): Promise<IdsNode[]> {
        const attr = this.getAttribute(attributeId);
        if (_.isEmpty(attr)) return [];

        if (attr.Type != "node") return [];

        const uniqueIds = await this.getAttributeDistinctValues(attributeId, location, valueFilterIDs);

        // The dataProvider does any caching for the Nodes
        const nodes = await this.dataProvider.getAttributeNodes(attributeId, <number[]>uniqueIds);

        return nodes;
    }

    // Helper function for Visualization Controls
    getLocalAttributeNodes(attributeId: string): IdsNode[] {
        return _.clone(this.dctUniqueLocalNodes[attributeId] || []);
    }

    getAttributeDistinctRowValues(attributeId: string, rows: IdsRow[]): number[] | string[] | Date[] {
        const dctNew: { [attributeId: string]: any[] } = {};
        this.updateLocalDistinctValues(attributeId, dctNew, rows);
        return _.clone(dctNew[attributeId] || []);
    }

    // Data Row Filtering API & Events
    setFilter(filterSpec: IdsFilterSpec): void {
        this.setFilters([filterSpec]);
    }

    setFilters(filters: IdsFilterSpec[]): void {
        const flts = _.filter(filters, (a) => !_.isEmpty(a));
        const arrFilterIds = _.map(flts, (a) => a.id);
        const dctIds = _.keyBy(arrFilterIds, (a) => a);
        const removed = _.remove(this.queueScope.filters, (a) => a.id in dctIds);
        _.forEach(flts, (a) => {
            this.queueScope.filters.push(_.cloneDeep(a));
        });
        this.QueueDataLoadUpdate();
    }

    clearFilters(filterId: string | string[]): void {
        let flts: string[] = [];
        if (_.isArray(filterId)) flts = filterId;
        else flts = [filterId];
        _.remove(flts, (a) => _.isEmpty(a));
        const dctIds = _.keyBy(flts, (a) => a);
        _.remove(this.queueScope.filters, (a) => a.id in dctIds);
        this.QueueDataLoadUpdate();
    }

    clearFiltersOfTypes(filterTypes: AttributeFilterType[]): void {
        const dctIds = _.keyBy(filterTypes, (a) => a);
        _.remove(this.queueScope.filters, (a) => a.type in dctIds);
        this.QueueDataLoadUpdate();
    }

    getFilter(filterId: string): IdsFilterSpec[] {
        return _.cloneDeep(_.filter(this.queueScope.filters, (a) => a.id == filterId));
    }

    getAllFilters(bLoaded?: boolean): IdsFilterSpec[] {
        return _.cloneDeep(bLoaded ? this.loadedScope.filters : this.queueScope.filters);
    }

    get OnFilterChanged(): BehaviorSubject<IMetricDataSet> {
        return this.onFilterChanged;
    }

    get FilteredRows(): IdsRow[] {
        return this.filteredRows;
    }

    get OnFilteredRowsChanged(): BehaviorSubject<IMetricDataSet> {
        return this.onFilteredRowsChanged;
    }

    get AllRows(): IdsRow[] {
        return this.allRows;
    }

    get OnAllRowsChanged(): BehaviorSubject<IMetricDataSet> {
        return this.onAllRowsChanged;
    }

    // Pause, Resume Data Loading
    pauseDataLoading(): void {
        this.bDataLoadingPaused = true;
    }

    resumeDataLoading(): void {
        this.bDataLoadingPaused = false;
        this.QueueDataLoadUpdate();
    }

    get DataLoadingPaused(): boolean {
        return this.bDataLoadingPaused;
    }

    // Access to Row Attribute Values,  Metric Values and Custom Values
    getRowAttribute(row: IdsRow, attributeId: string): any {
        if (attributeId in this.lclAttributes) {
            const atr = this.lclAttributes[attributeId];
            return atr.getComputedValue(this, row)
        }
        return this.dataProvider.getRowAttribute(row, attributeId);
    }

    getRowMetric(row: IdsRow, metricId: string): number {
        if (metricId in this.lclMetrics) {
            const mtr = this.lclMetrics[metricId];
            return mtr.getComputedValue(this, row)
        }
        return this.dataProvider.getRowMetric(row, metricId);
    }

    //-- Custom Data
    getRowData(row: IdsRow, keyId: string): any {
        return this.dataProvider.getRowData(row, keyId);
    }

    setRowData(row: IdsRow, keyId: string, value: any): void {
        this.dataProvider.setRowData(row, keyId, value);
    }

    getRowAggregateMetric(rows: IdsRow[], metricId: string): number {
        const self = this
        if(metricId in self.dpMetrics)
            return self.dataProvider.getRowAggregateMetric(rows, metricId);

        const mtr = self.lclMetrics[metricId]
        if(_.isEmpty(mtr?.aggregateExpression))
            return undefined;

        const aggExpr = mtr.aggregateExpression;

        function getSymbol(row: IdsRow, symbolId: string): number {
            return self.getRowMetric(row, symbolId)
        }

        const aggMtrValue = ExpressionUtils.getAggregate(aggExpr, rows, getSymbol)
        return aggMtrValue

    }

    //-- Private Functions

    //--
    private QueueDataLoadUpdate(): void {
        this.esUpdateData.Run();
    }

    // Important: This should only be called from the execution serializer
    private async loadData(): Promise<void> {
        const pp = this.loadProgressProvider;
        try {
            // 0. Check if Data Loading is Paused
            // 1. Use "Queue" LoadScope to create new "Loaded" & "Static" LoadScope objects
            // 2. Compare new "Loaded" Scope to current "Loaded" Scope
            // 3. Compare new "Static" Scope to current "Static" Scope
            // 4. if "Loaded" scope changed,
            // 4a. Load All Rows, Update All Rows, Reset Local Distinct Keys
            // 4b. Update Local Computed Attribute - All Rows Function
            // 4c. Update Local Computed Metrics - All Rows Function
            // 5. Execute Client Side Filters against Client Side Only Filters
            // 5a. Update Local Computed Attribute - Filtered Rows Function
            // 5b. Update Local Computed Metrics - Filtered Rows Function
            // 5c. Update Local Distinct Values and Nodes, to support synchronous Local Node API
            // 6. Maintain State
            // //  -- if "Static" scope changed, Reset Remote Distinct Keys
            // //  -- set allRows, filteredRows
            // //  -- set loadedScope & staticScope
            // 7. Raise Events based on changes made in (1-6)

            // 0. Check if Data Loading is Paused
            if (this.bDataLoadingPaused) {
                return;
            }

            // 1. Use "Queue" LoadScope to create new "Loaded" & "Static" LoadScope objects
            let allFilters = _.cloneDeep(this.queueScope.filters);
            // Sort so hashes are consistent
            allFilters = _.sortBy(allFilters, [(a) => a.type, (a) => a.id, (a) => sha1(a.condition)]);

            const newLoadScope: ILoadScope = {
                attributes: _.clone(this.queueScope.attributes),
                attributesDataProvider: [],
                attributesLocal: [],
                metrics: _.clone(this.queueScope.metrics),
                metricsDataProvider: [],
                metricsLocal: [],
                filters: [],
                rowThresholdCount: this.queueScope.rowThresholdCount,
                attributeHash: "",
                attributeDataProviderHash: "",
                attributeLocalHash: "",
                metricHash: "",
                metricsDataProviderHash: "",
                metricsLocalHash: "",
                filterHash: ""
            };
            _.forEach(newLoadScope.attributes, (a) => {
                if (a in this.dpAttributes) newLoadScope.attributesDataProvider.push(a);
                if (a in this.lclAttributes) newLoadScope.attributesLocal.push(a);
            });

            _.forEach(newLoadScope.metrics, (a) => {
                if (a in this.dpMetrics) newLoadScope.metricsDataProvider.push(a);
                if (a in this.lclMetrics) newLoadScope.metricsLocal.push(a);
            });


            // Static, Server Side and Client Filters that aren't in
            const dctAttr = _.keyBy(this.queueScope.attributes, (a) => a);
            const serverFilters: IdsFilterSpec[] = _.filter(
                allFilters,
                (a: IdsFilterSpec) => a.type == "static" || a.type == "server-side"
            );
            const clientOnlyFilters: IdsFilterSpec[] = [];
            const allClientFilters: IdsFilterSpec[] = _.filter(
                allFilters,
                (a: IdsFilterSpec) => a.type == "client-side"
            );

            // Determine if and where Client Side filters are evaluated
            // 1. Any client side filters that reference local metrics or local attributes need to be handled on the client side
            _.forEach(allClientFilters, (aFilter) => {
                let bServerSide = false;
                let bClientSideMandatory = false
                const symbols = FilterUtils.getSymbolReferences(aFilter.condition);

                // 1. Any client side filters that reference local metrics or local attributes need to be handled on the client side
                _.forEach(symbols, (s) => {
                    if ((s in this.lclMetrics) || (s in this.lclAttributes)) {
                        bClientSideMandatory = true;
                        return false;
                    }
                });

                if (bClientSideMandatory) {
                    clientOnlyFilters.push(aFilter)
                    return // Next Filter, skip dataset check
                }

                // 2. Any client side filters that can't be resolved using the resulting data set (i.e. missing attribute), needs to be pushed to the server

                _.forEach(symbols, (s) => {
                    if (!(s in dctAttr)) {
                        bServerSide = true;
                        return false;
                    }
                });

                if (bServerSide) serverFilters.push(aFilter);
                else clientOnlyFilters.push(aFilter);
            });
            newLoadScope.filters = serverFilters;

            const newStaticScope: ILoadScope = {
                attributes: [],
                attributesDataProvider: [],
                attributesLocal: [],
                metrics: [],
                metricsDataProvider: [],
                metricsLocal: [],
                filters: _.filter(allFilters, (a: IdsFilterSpec) => a.type == "static"),
                rowThresholdCount: this.queueScope.rowThresholdCount,
                attributeHash: "",
                attributeDataProviderHash: "",
                attributeLocalHash: "",
                metricHash: "",
                metricsDataProviderHash: "",
                metricsLocalHash: "",
                filterHash: ""
            };

            // 2. Compare new "Loaded" Scope to current "Loaded" Scope
            // 3. Compare new "Static" Scope to current "Static" Scope
            const bStaticScopeChanged = !this.LoadScope_equals(this.staticScope, newStaticScope);
            const bLoadScopeChanged = this.bForceReload || !this.LoadScope_equals(this.loadedScope, newLoadScope);
            const bDataProviderScopeChange =
                this.bForceReload || !this.LoadScope_equals(this.loadedScope, newLoadScope, true);
            this.bForceReload = false;

            pp.start("Loading");

            // 4. if "Loaded" scope changed,
            // 4a. Load All Rows, Update All Rows, Reset Local Distinct Keys
            let bAllRowsChanged = false;
            let bFilteredRowsChanged = false;
            let allRows: IdsRow[];
            if (bDataProviderScopeChange) {
                allRows = await this.dataProvider.getDataRows(
                    newLoadScope.attributesDataProvider,
                    newLoadScope.metricsDataProvider,
                    newLoadScope.filters,
                    newLoadScope.rowThresholdCount
                );
            } else {
                allRows = this.allRows;
            }
            bAllRowsChanged = bLoadScopeChanged;

            // 4b. Update Local Computed Attribute - All Rows Function
            if (bLoadScopeChanged && !_.isEmpty(newLoadScope.attributesLocal)) {
                // Set Computed Attributes
                for (const attrId of newLoadScope.attributesLocal) {
                    const atr = this.getAttribute(attrId);
                    if (atr.bAttributeComputed) {
                        if (_.isFunction(atr.setComputedValuesAllRows)) {
                            await atr.setComputedValuesAllRows(
                                this,
                                newLoadScope.attributes,
                                newLoadScope.metrics,
                                allRows
                            );
                        }
                    }
                }
            }
            // 4c. Update Local Computed Metrics - All Rows Function
            if (bLoadScopeChanged && !_.isEmpty(newLoadScope.metricsLocal)) {
                // Set Computed Metrics
                for (const mtrId of newLoadScope.metricsLocal) {
                    const mtr = this.getMetric(mtrId);
                    if (mtr.bMetricComputed) {
                        if (_.isFunction(mtr.setComputedValuesAllRows)) {
                            await mtr.setComputedValuesAllRows(
                                this,
                                newLoadScope.attributes,
                                newLoadScope.metrics,
                                allRows
                            );
                        }
                    }
                }
            }


            // 5. Execute Client Side Filters against Client Side Only Filters
            // We could optimize this, if no filter changes have been made
            const filterRows: IdsRow[] = this.Rows_filter(allRows, clientOnlyFilters);
            bFilteredRowsChanged = true;

            // 5a. Update Local Computed Attribute - Filtered Rows Function
            if (bFilteredRowsChanged && !_.isEmpty(newLoadScope.attributesLocal)) {
                // Set Computed Attributes
                for (const attrId of newLoadScope.attributesLocal) {
                    const atr = this.getAttribute(attrId);
                    if (atr.bAttributeComputed) {
                        if (_.isFunction(atr.setComputedValuesFilteredRows)) {
                            await atr.setComputedValuesFilteredRows(
                                this,
                                newLoadScope.attributes,
                                newLoadScope.metrics,
                                filterRows,
                                allRows
                            );
                        }
                    }
                }
            }

            // 5b. Update Local Computed Metrics - Filtered Rows Function
            if (bFilteredRowsChanged && !_.isEmpty(newLoadScope.metricsLocal)) {
                // Set Computed Metrics
                for (const mtrId of newLoadScope.metricsLocal) {
                    const mtr = this.getMetric(mtrId);
                    if (mtr.bMetricComputed) {
                        if (_.isFunction(mtr.setComputedValuesFilteredRows)) {
                            await mtr.setComputedValuesFilteredRows(
                                this,
                                newLoadScope.attributes,
                                newLoadScope.metrics,
                                filterRows,
                                allRows
                            );
                        }
                    }
                }
            }

            // 5c. Update Local Distinct Values and Nodes, to support synchronous Local Node API
            const dctNewDctUniqueLocalValues: { [attributeId: string]: any[] } = {};
            const dctNewUniqueLocalNodes: { [attributeId: string]: IdsNode[] } = {};
            if (bDataProviderScopeChange) {
                for (const attrId of newLoadScope.attributes) {
                    const attr = this.getAttribute(attrId);
                    if (attr.Type == "node") {
                        this.updateLocalDistinctValues(attrId, dctNewDctUniqueLocalValues, allRows);
                        dctNewUniqueLocalNodes[attrId] = await this.dataProvider.getAttributeNodes(
                            attrId,
                            dctNewDctUniqueLocalValues[attrId]
                        );
                    }
                }
            }

            // 6. Maintain State (nothing async) past this point
            // //  -- if "Static" scope changed, Reset Remote Distinct Keys
            // //  -- set allRows, filteredRows
            // //  -- set loadedScope & staticScope
            this.loadedScope = newLoadScope;
            this.staticScope = newStaticScope;
            this.allRows = allRows;
            this.filteredRows = filterRows;
            if (bStaticScopeChanged) this.dctUniqueRemoteValues = {};
            if (bDataProviderScopeChange) {
                this.dctUniqueLocalValues = dctNewDctUniqueLocalValues;
                this.dctUniqueLocalNodes = dctNewUniqueLocalNodes;
            }

            // 7. Raise Events based on changes made in (1-6)
            if (bAllRowsChanged)
                this.OnAllRowsChanged.next(this);

            if (bFilteredRowsChanged)
                this.OnFilteredRowsChanged.next(this);

            if (bAllRowsChanged || bFilteredRowsChanged || bStaticScopeChanged)
                this.OnFilterChanged.next(this);

            pp.finish();
        } catch (err) {
            pp.exception(err);
        }
    }

    private Rows_filter(AllRows: IdsRow[], filters: IdsFilterSpec[]): IdsRow[] {
        if (_.isEmpty(filters)) return _.clone(AllRows);

        const self = this;

        // Check Functions from Conditions
        function symbolGet(symbolId: string, row: IdsRow): any {
            if (symbolId in self.dpAttributes) return self.getRowAttribute(row, symbolId);

            if (symbolId in self.dpMetrics) return self.getRowMetric(row, symbolId);

            if (symbolId in self.lclAttributes) return self.getRowAttribute(row, symbolId);

            if (symbolId in self.lclMetrics) return self.getRowMetric(row, symbolId);
        }

        const cndFunctions = _.map(filters, (flt) => {
            return FilterUtils.createFunction(flt.condition, this.dctSimpleType, symbolGet);
        });

        // Check Functions
        const notFunctions = _.remove(cndFunctions, (a) => !_.isFunction(a));
        // Anything we can't make a function , simply skip for now

        const filterRows: IdsRow[] = [];
        _.forEach(AllRows, (row) => {
            let bInclude = true;
            _.forEach(cndFunctions, (fnc) => {
                const bRes = fnc(row);
                if (!bRes) {
                    bInclude = false;
                    return true; // stop iterating
                }
            });

            if (bInclude) filterRows.push(row);
        });

        return filterRows;
    }

    private LoadScope_equals(cur: ILoadScope, next: ILoadScope, bIgnoreLocalAttribsAndMetrics?: boolean): boolean {
        this.LoadScope_updateHash(next);

        if (_.isEmpty(cur) || _.isEmpty(next)) return false;


        if (bIgnoreLocalAttribsAndMetrics) {
            if (cur.attributeDataProviderHash != next.attributeDataProviderHash) return false;
        } else {
            if (cur.attributeHash != next.attributeHash) return false;
        }

        if (bIgnoreLocalAttribsAndMetrics) {
            if (cur.metricsDataProviderHash != next.metricsDataProviderHash) return false;
        } else {
            if (cur.metricHash != next.metricHash) return false;
        }


        if (cur.filterHash != next.filterHash) return false;

        if (cur.rowThresholdCount != next.rowThresholdCount) return false;

        return true;
    }

    private LoadScope_updateHash(ls: ILoadScope) {
        if (_.isEmpty(ls)) return;

        ls.attributeHash = sha1(ls.attributes);
        ls.attributeDataProviderHash = sha1(ls.attributesDataProvider);
        ls.attributeLocalHash = sha1(ls.attributesLocal);
        ls.metricHash = sha1(ls.metrics);
        ls.metricsDataProviderHash = sha1(ls.metricsDataProvider);
        ls.metricsLocalHash = sha1(ls.metricsLocal);
        ls.filterHash = sha1(ls.filters);
    }
}
